import { useLocation, useMatches, RemixBrowser } from '@remix-run/react'
import {
  init as sentryInit,
  browserTracingIntegration,
  replayIntegration,
  browserProfilingIntegration,
} from '@sentry/remix'
import i18next from 'i18next'
import { startTransition, StrictMode, useEffect } from 'react'
import { hydrateRoot } from 'react-dom/client'
import { I18nextProvider, initReactI18next } from 'react-i18next'

import i18nConfig from '~/locales/i18n.config'

async function hydrate() {
  await i18next.use(initReactI18next).init(i18nConfig)

  startTransition(() => {
    hydrateRoot(
      document,
      <I18nextProvider i18n={i18next}>
        <StrictMode>
          <RemixBrowser />
        </StrictMode>
      </I18nextProvider>
    )
  })
}

if (window.requestIdleCallback) {
  window.requestIdleCallback(hydrate)
} else {
  // Safari doesn't support requestIdleCallback
  // https://caniuse.com/requestidlecallback
  window.setTimeout(hydrate, 1)
}

export function initSentry() {
  const reduceSentryReporting =
    window.ENV.NODE_ENV === 'production' && window.ENV.IS_STAGING !== 'true'
  const sentryEnv =
    window.ENV.NODE_ENV === 'production'
      ? window.ENV.IS_STAGING === 'true'
        ? 'staging'
        : 'production'
      : 'development'

  sentryInit({
    dsn: window.ENV.SENTRY_DSN,
    environment: sentryEnv,
    beforeSend(event) {
      if (event.request?.url) {
        const url = new URL(event.request.url)
        if (
          url.protocol === 'chrome-extension:' ||
          url.protocol === 'moz-extension:'
        ) {
          // This error is from a browser extension, ignore it
          return null
        }
      }
      return event
    },
    integrations: [
      browserTracingIntegration({
        useEffect,
        useLocation,
        useMatches,
      }),
      replayIntegration(),
      browserProfilingIntegration(),
    ],

    tracesSampleRate: reduceSentryReporting ? 0.1 : 1,
    replaysSessionSampleRate: reduceSentryReporting ? 0.1 : 1,
    replaysOnErrorSampleRate: 1.0,
  })
}

if (window.ENV.SENTRY_DSN) {
  initSentry()
}
